import { Injectable } from '@angular/core';
import { ServiceBase } from 'services/servicebase';
import { HttpService } from 'services/http.service';
import { environment } from 'environments/environment';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { OptionModel } from 'services/models/model';
import { QueryParams } from 'services/models/odata/queryParams';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { BYPASS_LOG } from '@core/interceptors/loading-screen.interceptor';
import { Response } from 'services/models/response';
import { guidEmpty } from 'utils/helps';
import { FormItemModel } from 'services/models/form-builder/form-item.model';
import { FormModel } from 'services/models/form-builder/form.model';
import { FormSubmissionModel } from 'services/models/form-builder/form-submission.model';
import { FormSubmissionAnswerModel } from 'services/models/form-builder/form-submission-answer-model';
import { Filter } from 'services/models/odata/filter';
import { FormSubmissionStatusType } from 'services/models/form-builder/enum/form-submission-status-type.enum';

@Injectable()
export class FormBuilderService extends ServiceBase<FormModel> {
  constructor(
    http: HttpService,
    private httpClient: HttpClient
  ) {
    super('formbuilder', http, environment.apiUrl);
  }

  createEntity(input: any): FormModel {
    return FormModel.create(input);
  }

  listForms(queryParams: QueryParams): Observable<Response<FormModel[]>> {
    return this.http
      .get(
        `${this.endpoint}/list-form${this.createBuildQuery(queryParams)}`,
        null,
        null,
        null,
        true
      )
      .pipe(map(this.mapToResponse));
  }

  listFormsByType(type: number): Observable<Response<FormModel[]>> {
    const params = {
      select: ['id', 'title', 'enable', 'changeDate', 'status'],
      filter: Filter.equal('type', type),
    } as QueryParams;

    return this.http
      .get(`${this.endpoint}/list-form${this.createBuildQuery(params)}`, null, null, null, true)
      .pipe(map(this.mapToResponse));
  }

  private mapToResponse = (res: any): Response<FormModel[]> => {
    const response = new Response<FormModel[]>();
    response.data = res.body.map((pp: any) => FormModel.create(pp) as FormModel);
    response.count = res.headers.get('x-count');
    return response;
  };

  public getStructure(formId: string): Observable<FormModel> {
    return this.http
      .get(`${this.endpoint}/structure/${formId}`, null, null, null, true)
      .pipe(map(response => FormModel.create(response.body)));
  }

  public clone(formId: string): Observable<FormModel> {
    return this.http
      .get(`${this.endpoint}/structure/${formId}/clone`, null, null, null, true)
      .pipe(map(response => FormModel.create(response.body)));
  }

  public saveFormSession(isLastGroup: boolean, formData: any): Observable<any> {
    return this.http.post(`${this.endpoint}/fill-form/${isLastGroup}`, formData, null, null, true);
  }

  public createFormSubmission(form: any, anonymousRequest: boolean = false): Observable<any> {
    return this.http.post(
      `${this.endpoint}/form-submission/create/${anonymousRequest}`,
      form,
      null,
      null,
      true
    );
  }

  public getFormSubmission(
    formTypeId: string,
    employeeId: string,
    status: string = FormSubmissionStatusType.PENDING.value,
    disableLoanding: boolean = false
  ): Observable<FormSubmissionModel> {
    const params = new HttpParams().set('formTypeId', formTypeId).set('employeeId', employeeId);

    if (
      status !== FormSubmissionStatusType.CREATED.value &&
      status !== FormSubmissionStatusType.PENDING.value
    ) {
      params.set('status', status);
    }

    return this.http
      .get(`${this.endpoint}/form-submission/search`, { params }, null, null, disableLoanding)
      .pipe(map((data: any) => (data.body ? FormSubmissionModel.create(data.body) : null)));
  }

  public getAnswersById(formId: string): Observable<FormSubmissionAnswerModel[]> {
    return this.http
      .get(`${this.endpoint}/seek-answers/${formId}`, null, null, null, true)
      .pipe(map((data: any) => data.body.map(item => FormSubmissionAnswerModel.create(item))));
  }

  public _listOptions(query: QueryParams): Observable<OptionModel[]> {
    return this.http.get(`${this.endpoint}/list-form`, query, null, null, true).pipe(
      map((res: any) => {
        const data = res.body || res;

        return data.map((item: any) => ({
          label: item.title,
          value: item.id,
        }));
      })
    );
  }

  public removeFormElement(
    queryParams: { groupId: string; itemId: string; optionId: string },
    disableLoanding = true
  ): Observable<any> {
    const opt = {
      context: new HttpContext().set(BYPASS_LOG, disableLoanding),
      params: new HttpParams({ fromObject: queryParams }),
    };

    return this.httpClient
      .delete<any>(`${this.baseUrl}/${this.endpoint}/remove-form-element`, opt)
      .pipe(map(response => response));
  }

  public changeStatusForm(idObject: string): Observable<any> {
    return this.http.put(`${this.endpoint}/form-change-status/${idObject}`, {});
  }

  prepareFormData(
    formValue: any,
    items: FormItemModel[],
    formSubmissionId: string
  ): FormSubmissionAnswerModel[] {
    return Object.keys(formValue)
      .map(key => {
        const item = items.find(x => x.id === key);
        if (!item) return null;

        let value = formValue[key];

        if (typeof value === 'object' && !!value && !Array.isArray(value)) {
          value = value.label;
        }
        if (Array.isArray(value)) {
          value = value.map(m => m.label || m).join(';');
        }

        return {
          value,
          formSubmissionId,
          formItemId: item.id,
        } as FormSubmissionAnswerModel;
      })
      .filter(Boolean);
  }

  public createOrUpdate(payload: any) {
    if (payload.id && payload.id !== guidEmpty) {
      return this.updateForm(payload);
    }

    return this.createForm(payload);
  }

  public updateForm(form: any): Observable<any> {
    return this.http.put(`${this.endpoint}/update-form`, form);
  }

  public createForm(form: any): Observable<any> {
    return this.http.post(`${this.endpoint}/create-form`, form, null, null, true);
  }

  public getFormByTypeAndTitle(type: number, title: string): Observable<FormModel | null> {
    return this.http
      .get(
        `${this.endpoint}/list-form?$filter=type eq ${type} and title eq '${title}' and enable eq true`,
        null,
        null,
        null,
        true
      )
      .pipe(map(res => (res.body?.length ? FormModel.create(res.body[0]) : null)));
  }

  public getOrCreateSubmission(formTypeId: string, employeeId: string): Observable<any> {
    return this.http
      .get(
        `${this.endpoint}/form-submission/pending?formTypeId=${formTypeId}&employeeId=${employeeId}`,
        null,
        null,
        null,
        true
      )
      .pipe(
        map(res => {
          if (res.body) {
            return FormSubmissionModel.create(res.body);
          } else {
            const payload = { formTypeId, employeeId } as FormSubmissionModel;

            return this.createFormSubmission(payload).pipe(
              map(submission => FormSubmissionModel.create(submission))
            );
          }
        })
      );
  }

  public getFormSubmissionById(id: string): Observable<FormSubmissionModel> {
    const params = new HttpParams().set('id', id);

    return this.http
      .get(`${this.endpoint}/form-submission/search`, { params })
      .pipe(map((data: any) => (data.body ? FormSubmissionModel.create(data.body) : null)));
  }

  public getAnswersCount(
    queryParams: { groupId: string; itemId: string; optionId: string },
    disableLoanding = true
  ): Observable<number> {
    const params = new HttpParams()
      .set('groupId', queryParams.groupId)
      .set('itemId', queryParams.itemId)
      .set('optionId', queryParams.optionId);

    return this.http
      .get(`${this.endpoint}/answers/count`, { params }, null, null, disableLoanding)
      .pipe(map((data: any) => data.body));
  }

  public _listOptionsByType(params: QueryParams): Observable<OptionModel[]> {
    const query = this.createBuildQuery(params);
    return this.http.get(`${this.endpoint}/list-form${query}`, null, null, null, true).pipe(
      map((res: any) => {
        const data = res.body || res;

        return data.map((item: any) => ({
          label: item.title,
          value: item.id,
        }));
      })
    );
  }
}
